import React, { useReducer, useEffect } from 'react';
import { Modal, Button, Row, Col, Image } from 'react-bootstrap';
import FileUploader from "react-firebase-file-uploader";
import { TextInput } from 'evergreen-ui';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import firebase from 'firebase';
import 'firebase/firestore';
import uuid from 'uuid/v4';

import './editor.scss';

const initialState = {
    title: "",
    subtitle: "",
    content: "",
    created: null,
    updated: null,
    state: false,
    articleNumber: 0,
    pictureURL: null,
};

const editorReducer = (state, action) => {
    switch (action.type) {
        case 'UPDATE_TITLE':
            return { ...state, title: action.title };
        case 'UPDATE_SUBTITLE':
            return { ...state, subtitle: action.subtitle };
        case 'UPDATE_CONTENT':
            return { ...state, content: action.content };
        case 'UPDATE_PICTURE':
            console.log('updating picture');
            return state;
        case 'UPDATE_ARTICLE':
            return {
                ...state,
                title: action.article.title,
                subtitle: action.article.subtitle,
                content: action.article.content
            };
        case 'UPLOAD_PICTURE':
            return { ...state, isUploading: action.isUploading }
        case 'UPDATE_ARTICLE_NUMBER':
            return { ...state, articleNumber: action.articleNumber }
        case 'HANDLE_PROGRESS':
            return { ...state, pictureProgress: action.pictureProgress }
        case 'HANDLE_UPLOAD_SUCCESS':
            return { ...state, isUploading: action.isUploading, pictureURL: action.pictureURL }
        case 'SEND_ARTICLE':
            return initialState;
        default:
            return state;
    }
}

export default function Editor(props) {
    const articlesRef = firebase.firestore().collection('articles');

    const [article, dispatch] = useReducer(editorReducer, initialState);

    let modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image'],
            ['clean']
        ]
    }

    let formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image'
    ]

    // Getting articles count from firebase --stats--
    useEffect(() => {
        try {
            articlesRef.doc('--stats--').get().then(doc => {
                console.log("Nombre d'articles : ", doc.data().count);
                dispatch({ type: 'UPDATE_ARTICLE_NUMBER', articleNumber: (doc.data().count + 1) });
            });

        } catch (error) {
            console.log('error getting --stats-- count');
        }
    }, []);

    // Updating article state if editing an article
    useEffect(() => {
        if (props.article) {
            dispatch({ type: 'UPDATE_ARTICLE', article: props.article });
        }
    }, [props.article]);

    const updateTitle = (event) => dispatch({ type: 'UPDATE_TITLE', title: event.target.value });
    const updateSubtitle = (event) => dispatch({ type: 'UPDATE_SUBTITLE', subtitle: event.target.value });
    const updateContent = (content) => dispatch({ type: 'UPDATE_CONTENT', content: content });
    const handleUploadStart = () => dispatch({ type: 'UPLOAD_PICTURE', isUploading: true });
    const handleProgress = (pictureProgress) => dispatch({ type: 'HANDLE_PROGRESS', pictureProgress: pictureProgress });

    const sendDraft = (e) => {
        if (props.article) {
            console.log('updating article to drafts');
            articlesRef.doc(props.article.id).update({
                title: article.title,
                subtitle: article.subtitle,
                content: article.content,
                updated: Date.now(),
                state: false,
            });
            props.close();

            alert('Modifications enregistrées.');
        } else {
            console.log('sending article to drafts');
            e.preventDefault();
            try {
                const id = uuid();
                articlesRef.doc(id).set({
                    id: id,
                    viewsCount: 0,
                    title: article.title,
                    subtitle: article.subtitle,
                    content: article.content,
                    state: false,
                    created: Date.now(),
                    updated: Date.now(),
                    pictureURL: article.pictureURL,
                    articleNumber: article.articleNumber,
                }).then(() => {
                    articlesRef.doc('--stats--').update({ count: (article.articleNumber) });
                });
                console.log('data sent to drafts');
                dispatch({ type: 'SEND_ARTICLE' });

                props.close();

                alert('Article enregistrées dans les brouillons.');

            } catch (error) {
                console.log("error uploading article : ", error);
            }
        }

    };

    const sendOnline = (e) => {
        if (props.article) {
            console.log('updating article to online');
            articlesRef.doc(props.article.id).update({
                title: article.title,
                subtitle: article.subtitle,
                content: article.content,
                updated: Date.now(),
                state: true
            }).then(() => {
                props.close();
            });
        } else {
            console.log('sending article to online');
            e.preventDefault();
            try {
                const id = uuid();
                articlesRef.doc(id).set({
                    id: id,
                    viewsCount: 0,
                    title: article.title,
                    subtitle: article.subtitle,
                    content: article.content,
                    state: true,
                    created: Date.now(),
                    updated: Date.now(),
                    pictureURL: article.pictureURL,
                    articleNumber: article.articleNumber,
                }).then(() => {
                    articlesRef.doc('--stats--').update({ count: (article.articleNumber) });
                });
                console.log('data sent to firebase');
                dispatch({ type: 'SEND_ARTICLE' });
                props.close();
            } catch (error) {
                console.log("error uploading article : ", error);
            }
        }

    };

    const handleUploadError = () => {
        alert("Erreur lors de l'envoi de l'image");
        dispatch({ type: 'UPLOAD_PICTURE', isUploading: false });
    };

    const handleUploadSuccess = (filename) => {
        firebase.storage().ref('images').child(filename).getDownloadURL().then((url) => {
            dispatch({ type: 'HANDLE_UPLOAD_SUCCESS', isUploading: false, pictureURL: url });
        });
    };

    return (
        <Modal show={props.show} onHide={props.close} className="editor-modal">
            <Modal.Header>
                <Modal.Title>Nouvel article</Modal.Title>
            </Modal.Header>
            <Modal.Body className="root">
                <Row>
                    <TextInput
                        name="title-input"
                        className="title-input"
                        placeholder="Titre"
                        onChange={updateTitle}
                        value={article.title}
                    />
                </Row>
                <Row>
                    <TextInput
                        name="subtitle-input"
                        className="title-input"
                        placeholder="Sous-titre"
                        onChange={updateSubtitle}
                        value={article.subtitle}
                    />
                </Row>

                <Row>
                    <ReactQuill theme="snow" value={article.content} modules={modules} formats={formats} onChange={updateContent} style={{ minHeight: 200 }} />
                </Row>

                <Row className="file-uploader-section">
                    <label>
                        Sélectionnez l'image de l'article
                        <FileUploader
                            hidden
                            accept="image/*"
                            name="article-picture"
                            randomizeFilename
                            storageRef={firebase.storage().ref("images")}
                            onUploadStart={handleUploadStart}
                            onUploadError={handleUploadError}
                            onUploadSuccess={handleUploadSuccess}
                            onProgress={handleProgress}
                        />
                    </label>

                    {article.pictureURL && <Image src={article.pictureURL} rounded />}
                    {article.isUploading && <p>Envoi en cours...</p>}
                    {article.pictureProgress && <p>{article.pictureProgress}%</p>}
                </Row>


                <Row>
                    <Button variant="light" onClick={sendDraft}>Enregistrer le brouillon</Button>
                    <Button variant="success" onClick={sendOnline}>Publier</Button>
                    <Button variant="warning" onClick={props.close}>Fermer</Button>
                </Row>

            </Modal.Body>
        </Modal>
    )
}
